import { normalizeText } from '@vif/core';
import JSZip from 'jszip';
import { Parser } from 'htmlparser2';
import { ParserError } from './errors.js';
import { assertByteLimit, finalizeDocument } from './limits.js';
import { ZipExpansionBudget, assertZipEntryCount, readZipEntryText, } from './zip.js';
/**
 * docx-lite (D-7, PRD §8.5): word/document.xml only, streamed through
 * htmlparser2 in XML mode. Fidelity contract (documented in-app):
 *  - tracked changes read as-if-accepted: w:ins content included, w:del /
 *    w:delText skipped;
 *  - comments, footnotes/endnotes, headers/footers, images and textbox
 *    content skipped (comment/footnote text lives outside document.xml;
 *    anchors carry no text; w:txbxContent is skipped explicitly);
 *  - tables flattened to cell paragraphs in document order;
 *  - headings best-effort from paragraph styles: pStyle HeadingN plus the
 *    locale-independent w:outlineLvl (localized style ids like "Balk1").
 */
const HEADING_STYLE = /^Heading([1-9])$/i;
/** Subtrees whose entire content is outside the fidelity contract. Deleted
 * text (w:delText), field instructions (w:instrText) and footnote/comment
 * anchors are excluded already: only w:t content is ever captured. */
const SKIPPED_SUBTREES = new Set(['w:del', 'w:moveFrom', 'w:txbxContent']);
export async function parseDocx(data, options) {
    assertByteLimit(data.byteLength);
    let zip;
    try {
        zip = await JSZip.loadAsync(data);
    }
    catch {
        throw new ParserError('corrupt-file');
    }
    // M-03: bound the declared entry count, and inflate document.xml through the
    // budgeted incremental reader so a decompression bomb aborts mid-inflate.
    const budget = new ZipExpansionBudget();
    assertZipEntryCount(Object.keys(zip.files).length, budget.limits);
    const documentXml = zip.file('word/document.xml');
    if (documentXml === null)
        throw new ParserError('corrupt-file');
    const xml = await readZipEntryText(documentXml, budget, options);
    return finalizeDocument(extractBlocks(xml));
}
function extractBlocks(xml) {
    const blocks = [];
    let paragraphText = '';
    let inParagraph = false;
    let headingLevel;
    let capturingText = false;
    let skipDepth = 0;
    const flushParagraph = () => {
        const text = normalizeText(paragraphText.replace(/\s+/g, ' ').trim());
        paragraphText = '';
        if (text.length === 0)
            return;
        if (headingLevel !== undefined) {
            blocks.push({ kind: 'chapter-heading', text, title: text, level: headingLevel });
        }
        else {
            blocks.push({ kind: 'paragraph', text });
        }
    };
    const parser = new Parser({
        onopentag(name, attribs) {
            if (SKIPPED_SUBTREES.has(name)) {
                skipDepth++;
                return;
            }
            if (skipDepth > 0)
                return;
            switch (name) {
                case 'w:p':
                    inParagraph = true;
                    paragraphText = '';
                    headingLevel = undefined;
                    break;
                case 'w:pStyle': {
                    const match = HEADING_STYLE.exec(attribs['w:val'] ?? '');
                    if (match !== null)
                        headingLevel = Number(match[1]);
                    break;
                }
                case 'w:outlineLvl': {
                    const level = Number(attribs['w:val']);
                    if (Number.isInteger(level) && level >= 0 && level <= 8) {
                        headingLevel = Math.min(level + 1, 9);
                    }
                    break;
                }
                case 'w:t':
                    capturingText = true;
                    break;
                case 'w:tab':
                case 'w:br':
                    if (inParagraph)
                        paragraphText += ' ';
                    break;
            }
        },
        ontext(text) {
            if (skipDepth === 0 && inParagraph && capturingText)
                paragraphText += text;
        },
        onclosetag(name) {
            if (SKIPPED_SUBTREES.has(name)) {
                skipDepth = Math.max(0, skipDepth - 1);
                return;
            }
            if (skipDepth > 0)
                return;
            if (name === 'w:t')
                capturingText = false;
            else if (name === 'w:p' && inParagraph) {
                inParagraph = false;
                flushParagraph();
            }
        },
    }, { xmlMode: true });
    parser.write(xml);
    parser.end();
    return blocks;
}
//# sourceMappingURL=docx.js.map